import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

const TRACEABILITY_ACCENT_INDEX = 3;

export function ProductLedgerPreview(): React.JSX.Element {
  const { t } = useTranslation('home');
  const rows = [
    {
      label: t('homePage.productMockups.ledger.rows.proforma.label'),
      amount: t('homePage.productMockups.ledger.rows.proforma.amount'),
      state: t('homePage.productMockups.ledger.rows.proforma.state'),
    },
    {
      label: t('homePage.productMockups.ledger.rows.installment.label'),
      amount: t('homePage.productMockups.ledger.rows.installment.amount'),
      state: t('homePage.productMockups.ledger.rows.installment.state'),
    },
    {
      label: t('homePage.productMockups.ledger.rows.voucher.label'),
      amount: t('homePage.productMockups.ledger.rows.voucher.amount'),
      state: t('homePage.productMockups.ledger.rows.voucher.state'),
    },
  ];
  const traceabilityItems = [
    t('homePage.productMockups.ledger.traceability.items.lead'),
    t('homePage.productMockups.ledger.traceability.items.proforma'),
    t('homePage.productMockups.ledger.traceability.items.payment'),
    t('homePage.productMockups.ledger.traceability.items.audit'),
  ];

  return (
    <div className="rounded-[1.75rem] border border-[var(--ir-line)] bg-[var(--ir-deep)] p-3 shadow-[0_24px_70px_-54px_rgba(0,31,34,0.35)]">
      <div className="rounded-[1.25rem] border border-white/10 bg-[var(--ir-parchment)] p-4 text-[var(--ir-ink)] md:p-5">
        <div className="mb-4 flex items-start justify-between gap-4 border-b border-[var(--ir-line)] pb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--ir-terra-ink)]">
              {t('homePage.productMockups.ledger.kicker')}
            </p>
            <p className="mt-1 font-serif text-2xl font-bold leading-none text-[var(--ir-ink)]">
              {t('homePage.productMockups.ledger.title')}
            </p>
          </div>
          <span className="rounded-full border border-[var(--ir-forest)]/20 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-[var(--ir-forest)]">
            {t('homePage.productMockups.ledger.badge')}
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[0.85fr_0.9fr_auto] items-center gap-3 rounded-xl border border-[var(--ir-line)] bg-white px-3 py-3 text-xs"
              >
                <span className="font-bold text-[var(--ir-ink)]">
                  {row.label}
                </span>
                <span className="font-mono text-xs text-[var(--ir-muted)]">
                  {row.amount}
                </span>
                <span className="rounded-full bg-[var(--ir-mint-pale)] px-2 py-1 text-xs font-bold uppercase tracking-[0.04em] text-[var(--ir-forest)]">
                  {row.state}
                </span>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-[var(--ir-line)] bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--ir-muted)]">
              {t('homePage.productMockups.ledger.traceability.title')}
            </p>
            <div className="mt-4 space-y-3">
              {traceabilityItems.map((item, index) => (
                <div key={item} className="flex items-center gap-3">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      index === TRACEABILITY_ACCENT_INDEX
                        ? 'bg-[var(--ir-terra)]'
                        : 'bg-[var(--ir-forest)]'
                    )}
                  />
                  <span className="text-xs font-medium text-[var(--ir-muted)]">
                    {item}
                  </span>
                  <span className="ml-auto h-px flex-1 bg-[var(--ir-line)]" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-[var(--ir-line)] bg-white px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--ir-muted)]">
              {t('homePage.productMockups.ledger.nextActionLabel')}
            </span>
            <span className="text-xs font-bold text-[var(--ir-ink)]">
              {t('homePage.productMockups.ledger.nextAction')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
